/// <amd-module name="Repos/Tfvc/Views/FilesHub/FilesHubViewService" />
import { IAnnotationBlock, IAnnotationHistoryEntry } from "Repos/Common/Contracts/Editor";
import {
    FileContentMetadata,
    TfvcItem,
    TfvcVersionDescriptor,
    TfvcVersionOption,
    TfvcVersionType,
    VersionControlRecursionType
} from "Repos/Common/Contracts/Git";
import { DiffViewerOrientation } from "Repos/Common/Contracts/VersionControlViewModel";
import { getTfvcResourceUri } from "Repos/Common/Util/Editor";
import { getFileExtension, getFileName, getIconNameForFile, getParentPath, trimLeadingSlash, trimTrailingSlash } from "Repos/Common/Util/File";
import { getTfvcVersionString, isLatest, tfvcVersionStringToVersionDescriptor } from "Repos/Common/Util/Version";
import { IVCViewModelDataProviderService } from "Repos/Common/VCViewModelDataProviderService";
import { MarkdownFilesHubUrlTransformer } from "Repos/Files/Components/ContentPreview";
import { IFilesHubUrlTransformer, IFolderContents, IGitItem, IGitItemContent } from "Repos/Files/Contracts";
import { loadAnnotationBlocks } from "Repos/FilesHubCommon/Annotate";
import { IFilesHubCommonService } from "Repos/FilesHubCommon/Contracts";
import { FileEditContext } from "Repos/FilesHubCommon/FileEditContext";
import { getItemCommonMenuItems } from "Repos/FilesHubCommon/FilesHubMenuItems";
import { FilesHubTabsServiceBase, IFilesHubTabRegistration, IFilesHubTabsService } from "Repos/FilesHubCommon/FilesHubTabs";
import { getReposMessagesService } from "Repos/Messages/ReposMessagesService";
import { getTfvcRoutingService } from "Repos/Tfvc/Common/TfvcRoutingService";
import { getTfvcFilesService } from "Repos/Tfvc/Files/TfvcFilesService";
import * as Resources from "Repos/Tfvc/Views/FilesHub/Resources";
import { IOptionsViewService } from "Repos/Views/Settings/Options/Services/OptionsViewService";
import { getTfvcClient } from "RestClient/Tfvc";
import { ITfsNavigationService } from "Tfs/Platform/Navigation";
import { getTFSPageData } from "Tfs/Platform/Page";
import {
    IReadonlyObservableArray,
    IReadonlyObservableValue,
    IReadyableReadonlyObservableArray,
    ObservableArray,
    ObservableCollection,
    ObservableValue,
    ReadyableObservableArray
} from "VSS/Core/Observable";
import { format, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { HistoryBehavior, IPageStateHistoryService } from "VSS/Features/History";
import { addMenuItems } from "VSS/Features/PlatformUI/ContributedMenu";
import { showMessageDialog } from "VSS/Features/PlatformUI/MessageDialog";
import { IUnsavedChangesService } from "VSS/Features/PlatformUI/UnsavedChangesService";
import { IVssPageContext, IVssService, Services, setRemoteSerializationSettings, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { IVssPageService } from "VSS/Platform/Page";
import { tryGetLocalStorageValue, trySetLocalStorageValue } from "VSS/Platform/Util/Storage";
import { combineUrlPaths, IQueryParameter, toQueryString, Uri } from "VSS/Platform/Util/Url";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IMenuItem } from "VSSUI/Menu";
import { MessageCardSeverity } from "VSSUI/MessageCard";
import { AnnotateDiffParameters, getLegacyTfvcClient, IChangeset } from "./RestClient/LegacyTfvc";
const tfvcRoot = "$/";
const visualStudioSolutionExtension = "sln";

/**
 * Item (file or folder) for use in the Explorer view
 */
export interface ITfvcItem extends IGitItem {
    changesetId: number;
    encoding: number;
    hashValue?: string;
    size?: number;
    isPendingChange?: boolean;
    isBranch?: boolean;
    deletionId?: number;
}

interface IInternalItemDescriptor extends TfvcItem {
    isInvalid: boolean;
    isNewItem: boolean;
}

/**
 * Options to get an item URL.
 */
export interface ItemUrlOptions {
    /**
     * Version of the item to include in the URL. By default, it will use current version.
     */
    version?: string;

    /**
     * Whether the URL should be absolute or relative.
     */
    absoluteUrl?: boolean;

    /**
     * Id of the files hub tab to navigate to. If not provided, it will use default tab on page loading.
     */
    tabId?: string;

    /**
     * The anchor to display when navigating to the URL, if the target item has preview (Markdown).
     */
    anchor?: string;
}

/**
 * Service for getting view data for the FilesHub view
 */
export interface IFilesHubViewService extends IVssService, IFilesHubCommonService {
    /**
     * Gets the diff orientation (side-by-side or inline) to use in the Files hub
     */
    getDiffOrientation(): IReadonlyObservableValue<DiffViewerOrientation>;

    /**
     * Gets the menu item to use for toggling diff orientation
     */
    getDiffOrientationMenuItem(): IHeaderCommandBarItem;

    /**
     * Gets the url to download the specified file at the specified version
     *
     * @param path Full path of the file
     * @param version Version at which to get the file
     * @param forceDownload If true, get the URL which forces a browser download/save operation. If false, the URL is one that the browser can "view" -- it can be used as an img src, for example.
     */
    getFileDownloadUrl(path: string, version?: TfvcVersionDescriptor, forceDownload?: boolean): string;

    /**
     * Gets the content information for the specified file
     *
     * @param path File path
     * @param version version of the file
     */
    getItemContent(path: string, version?: TfvcVersionDescriptor): IReadonlyObservableValue<IGitItemContent | undefined>;

    /**
     * Gets the annotation blocks for the currently selected file in the Files hub
     */
    getSelectedItemAnnotationBlocks(): IReadyableReadonlyObservableArray<IAnnotationBlock>;

    /**
     * Gets the currently selected git version descriptor
     */
    getVersion(): TfvcVersionDescriptor;

    /**
     * Sets the diff orientation (side-by-side or inline) to use in the Files hub
     */
    setDiffOrientation(orientation: DiffViewerOrientation): void;
}

/**
 * Context data passed to third-party menu contributions in the Files hub
 */
interface IFilesHubContributedMenuContext {
    gitRepository: null;
    item: {
        sourceProvider: "Tfvc";
        path: string;
        isFolder: boolean;
        isSymLink: boolean;
        item: {
            changeDate: Date;
            changeset: number;
            contentMetadata: FileContentMetadata;
            deletionId: number;
            isBranch: boolean;
            isPendingChange: boolean;
            serverItem: string;
            url: string;
            version: string;
            versionDescription: string;
        };
        url: string;
    };
    version: string;
    getSourceItemContext: () => IFilesHubContributedMenuContext;
}

const explorerModeSettingsKey = "Repos.FilesHub.ExplorerPane";

class FilesHubViewService extends VssService implements IFilesHubViewService {
    private diffOrientation: ObservableValue<DiffViewerOrientation>;
    private editMode: ObservableValue<boolean>;
    private explorerCollapsed: ObservableValue<boolean>;
    private itemsByPath: { [path: string]: ITfvcItem } = {};
    private itemsByParentPath: { [parentPath: string]: ReadyableObservableArray<ITfvcItem> } = {};
    private readonly loadedFolders = new ObservableArray<IFolderContents>();
    private optionsSvc: IOptionsViewService;
    private projectId: string = "";
    private projectName: string = "";
    private selectedItem: ObservableValue<ITfvcItem>;
    private selectedItemContent: ObservableValue<IGitItemContent | undefined> | undefined;
    private selectedItemAnnotationBlocks: ReadyableObservableArray<IAnnotationBlock> | undefined;
    private tfvcWebEditable: boolean;
    private version: TfvcVersionDescriptor;
    private versionString: string;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        const vcViewModelDataProviderService = this.pageContext.getService<IVCViewModelDataProviderService>("IVCViewModelDataProviderService");
        const userPreferences = vcViewModelDataProviderService.getUserPreferences();
        this.diffOrientation = new ObservableValue(userPreferences.diffViewerOrientation || DiffViewerOrientation.SideBySide);

        this.explorerCollapsed = new ObservableValue<boolean>(tryGetLocalStorageValue(explorerModeSettingsKey) === "collapsed");

        const project = getTFSPageData(this.pageContext)!.project;
        if (project) {
            this.projectId = project.id;
            this.projectName = project.name;
        }

        const pageStateHistoryService = this.pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");
        const historyState = pageStateHistoryService.getProjectedState();
        const path = this.fixPath(historyState["path"] || this.getRootItem().path);
        const createIfNew = historyState["createIfNew"] === "true";
        this.versionString = historyState["version"] || "T";
        this.version = tfvcVersionStringToVersionDescriptor(this.versionString);
        this.optionsSvc = this.pageContext.getService<IOptionsViewService>("OptionsViewService");
        this.optionsSvc.getRepositoryOptions(this.projectId, undefined, true).then(option => {
            this.tfvcWebEditable = option[0].value;
        });
        if (!isLatest(this.version)) {
            getReposMessagesService(pageContext).showMessage({
                buttons: [
                    {
                        id: "show-root",
                        onClick: () => {
                            const url = this.getItemUrl(this.getSelectedItem().value.path, {
                                version: "T",
                                tabId: getFilesHubTabsService(pageContext).getSelectedTabId().value
                            });
                            FastPageSwitch(pageContext, url, true);
                        },
                        text: Resources.ViewLatest
                    }
                ],
                renderMessage: () => format(Resources.ViewingVersionAtChangeset, this.version.version),
                severity: MessageCardSeverity.Info
            });
        }

        this.getChildItems(path, false, createIfNew);

        const selectedItem = this.getItem(path);
        if (selectedItem.isInvalid && createIfNew) {
            selectedItem.isNewItem = true;
            this.itemsByPath[selectedItem.path] = selectedItem;
        }

        this.selectedItem = new ObservableValue(selectedItem);
        this.editMode = new ObservableValue(!!selectedItem.isNewItem);

        pageStateHistoryService.registerObservable<IGitItem>(
            this.selectedItem,
            "path",
            HistoryBehavior.newEntry,
            item => {
                // Do not remove ?path=$/Project from URL when it's not necessary, that would add an extra history step
                // Note this won't happen in Git because DataProvider brings items since the very beginning,
                // so the first root item won't ever change. While in TFVC the initial root item is invalid until
                // we fetch, which replaces it and triggers a selectedItem change that we don't want to apply to URL.
                if (item.path === this.getRootItem().path && historyState["path"] !== item.path) {
                    return "";
                }

                return item.path;
            },
            path => {
                // Selected item path changed from the URL. Ensure that
                // we fully process the change by calling setSelectedItem.
                path = this.fixPath(path);
                const item = this.getItem(path);
                this.setSelectedItem(item);
                return item;
            }
        );

        this.pageContext.getService<IUnsavedChangesService>("IUnsavedChangesService").register({
            isDirty: () => {
                return !!(
                    this.editMode.value &&
                    this.selectedItemContent &&
                    this.selectedItemContent.value &&
                    this.selectedItemContent.value.editContext &&
                    (this.selectedItemContent.value.editContext.getIsDirty().value || this.selectedItem.value.isNewItem)
                );
            },
            getDirtyMessage: () => {
                return format(Resources.UnsavedFileFormat, this.selectedItem.value.path);
            }
        });
    }

    public getAllFiles(): IReadyableReadonlyObservableArray<IGitItem> {
        // There's no ahead search in TFVC, so this will be always empty.
        return new ReadyableObservableArray([]);
    }

    public dropFiles(folderItem: IGitItem, dataTransfer: DataTransfer): void {
        this.executeFileActionCommand("upload", folderItem, dataTransfer.files);
    }

    public getCloneMenuItems(): IMenuItem[] {
        return []; // No clone in TFVC
    }

    public getDiffOrientation(): IReadonlyObservableValue<DiffViewerOrientation> {
        return this.diffOrientation;
    }

    public getEditMode(): IReadonlyObservableValue<boolean> {
        return this.editMode;
    }

    public setEditMode(editMode: boolean): void {
        if (editMode !== this.editMode.value) {
            this.editMode.value = editMode;
        }
    }

    public getVersion(): TfvcVersionDescriptor {
        return this.version;
    }

    public setDiffOrientation(orientation: DiffViewerOrientation): void {
        if (this.diffOrientation.value !== orientation) {
            this.diffOrientation.value = orientation;

            const vcViewModelDataProviderService = this.pageContext.getService<IVCViewModelDataProviderService>("IVCViewModelDataProviderService");
            vcViewModelDataProviderService.setUserPreferences({
                ...vcViewModelDataProviderService.getUserPreferences(),
                diffViewerOrientation: orientation
            });
        }
    }

    public getExplorerCollapsed(): IReadonlyObservableValue<boolean> {
        return this.explorerCollapsed;
    }

    public setExplorerCollapsed(collapsed: boolean): void {
        this.explorerCollapsed.value = collapsed;
        trySetLocalStorageValue(explorerModeSettingsKey, collapsed ? "collapsed" : undefined);
    }

    public getSelectedItem(): IReadonlyObservableValue<ITfvcItem> {
        return this.selectedItem;
    }

    public setSelectedItem(item: ITfvcItem, editMode?: boolean): void {
        if (item !== this.selectedItem.value) {
            this.pageContext.getService<IUnsavedChangesService>("IUnsavedChangesService").triggerNavigateAway(
                () => {
                    // Switch off edit mode when changing files unless requested to turn it on
                    if (!!editMode !== this.editMode.value) {
                        this.editMode.value = !!editMode;
                    }

                    this.updateSelectedItem(item);
                },
                () => {
                    // Set the selected item in order to allow components to react to the cancelled selection
                    this.selectedItem.value = this.selectedItem.value;
                }
            );
        } else if (editMode !== undefined && editMode !== this.editMode.value) {
            this.editMode.value = editMode;
        }
    }

    public setSelectedPath(path: string): void {
        path = this.fixPath(path);
        const item = this.itemsByPath[path];
        if (item) {
            this.setSelectedItem(item);
        } else {
            const items = this.getChildItems(path);
            items.ready.subscribe(() => {
                const resolvedItem = this.getItem(path);
                this.setSelectedItem(resolvedItem);
            });
        }
    }

    public getLoadedFolders(): IReadonlyObservableArray<IFolderContents> {
        return this.loadedFolders;
    }

    public getChildItems(path: string, ensureLastChanges?: boolean, createIfNew?: boolean): IReadyableReadonlyObservableArray<ITfvcItem> {
        let childItems = this.itemsByParentPath[path];
        if (!childItems) {
            childItems = new ReadyableObservableArray<ITfvcItem>();
            this.itemsByParentPath[path] = childItems;

            const neededPaths: string[] = [path];

            // Resolve the parent paths of this path at the same time in order to be able to populate the source explorer tree
            let parentPath = getParentPath(path, tfvcRoot);
            while (parentPath) {
                if (this.itemsByParentPath[parentPath]) {
                    break;
                }

                // We only load $/ at collection level (cross-project)
                if (path === tfvcRoot && this.projectId) {
                    break;
                }

                neededPaths.push(parentPath);
                parentPath = getParentPath(parentPath, tfvcRoot);
            }

            getTfvcClient(this.pageContext)
                .getItemsBatch(
                    {
                        includeContentMetadata: false,
                        includeLinks: false,
                        itemDescriptors: neededPaths.map(path => {
                            return {
                                path,
                                recursionLevel: VersionControlRecursionType.OneLevelPlusNestedEmptyFolders,
                                ...this.version
                            };
                        })
                    },
                    this.projectId
                )
                .then(
                    results => {
                        const itemsMap: { [path: string]: IInternalItemDescriptor[] } = {};
                        let lastInvalidItem: IInternalItemDescriptor | undefined;
                        for (let i = 0, l = neededPaths.length; i < l; i++) {
                            const itemPath = neededPaths[i];
                            let childItems = results[i] as IInternalItemDescriptor[];
                            let currentInvalidItem: IInternalItemDescriptor | undefined = undefined;
                            if (!childItems || !childItems.length) {
                                // Item does not exist, create an invalid item entry
                                currentInvalidItem = {
                                    path: itemPath,
                                    isFolder: i > 0,
                                    isInvalid: true,
                                    isNewItem: createIfNew
                                } as IInternalItemDescriptor;
                                childItems = [currentInvalidItem];
                            }
                            if (createIfNew && lastInvalidItem) {
                                // The last item is a non-existing "new" entry. Add it to its parent folder
                                childItems.push(lastInvalidItem);
                            }
                            lastInvalidItem = currentInvalidItem;
                            itemsMap[itemPath] = childItems;
                        }
                        this.processItems(itemsMap);

                        if (ensureLastChanges) {
                            this.ensureChangesetsFetched(path);
                        }
                    },
                    () => {
                        this.processItems({ [path]: [{ path, isFolder: false, isInvalid: true } as IInternalItemDescriptor] });
                    }
                );
        } else if (ensureLastChanges) {
            this.ensureChangesetsFetched(path);
        }

        return childItems;
    }

    public getDiffOrientationMenuItem(): IHeaderCommandBarItem {
        const orientation = this.getDiffOrientation().value;
        const orientationLabel = orientation === DiffViewerOrientation.SideBySide ? Resources.SideBySideDiffTooltip : Resources.InlineDiffTooltip;

        return {
            ariaLabel: orientationLabel,
            iconProps: {
                iconName: orientation === DiffViewerOrientation.SideBySide ? "DiffSideBySide" : "DiffInline"
            },
            id: "diffOrientation",
            subMenuProps: {
                id: "diffOrientationSubMenu",
                items: [
                    {
                        iconProps: {
                            iconName: "DiffSideBySide"
                        },
                        id: "diffOrientationSideBySide",
                        onActivate: () => {
                            this.setDiffOrientation(DiffViewerOrientation.SideBySide);
                        },
                        text: Resources.SideBySideDiff
                    },
                    {
                        iconProps: {
                            iconName: "DiffInline"
                        },
                        id: "diffOrientationInline",
                        onActivate: () => {
                            this.setDiffOrientation(DiffViewerOrientation.Inline);
                        },
                        text: Resources.InlineDiff
                    }
                ]
            },
            subtle: true,
            text: orientation === DiffViewerOrientation.SideBySide ? Resources.SideBySideDiff : Resources.InlineDiff,
            tooltipProps: { text: orientationLabel }
        };
    }

    public getItemMenuItems(item: ITfvcItem, menuContributionIds?: string[], webEditingEnabled = true): IReadonlyObservableArray<IMenuItem> {
        const collection = new ObservableCollection<IMenuItem>();
        const canContribute = isFeatureFlagEnabled(this.pageContext, "WebAccess.VersionControl.Editing", true) && isLatest(this.version);
        // TODO Is there something like IReposPermissionsService for TFVC?

        let hideEditAction = false;
        if (item.path === this.selectedItem.value.path) {
            hideEditAction = !!(
                this.selectedItemContent &&
                this.selectedItemContent.value &&
                (this.selectedItemContent.value.details.metadata.isBinary || this.selectedItemContent.value.details.contentTruncated)
            );
        }

        if (!item.isInvalid) {
            if (canContribute) {
                collection.push(
                    getItemCommonMenuItems(item.isFolder, item.path === this.getRootItem().path, hideEditAction, commandName =>
                        this.getCommandExecutor(commandName, webEditingEnabled, item)
                    )
                );
            }

            collection.push([
                {
                    groupKey: "item-download",
                    iconProps: { iconName: "Download" },
                    id: "download",
                    onActivate: () => {
                        window.open(this.getDownloadUrlForItem(item.path, this.version, item.isFolder, true), "_blank", "noopener");
                    },
                    text: item.isFolder ? Resources.DownloadAsZip : Resources.Download
                }
            ]);

            if (getFileExtension(item.path) === visualStudioSolutionExtension && item.path === this.selectedItem.value.path) {
                // We only get the vsLink for selected item, as it comes with file content
                collection.push([
                    {
                        groupKey: "item-download",
                        iconProps: { iconName: "VisualStudioLogo" },
                        id: "open-in-vs",
                        onActivate: () => {
                            if (item.path !== this.selectedItem.value.path) {
                                return;
                            }

                            const itemContent = this.getSelectedItemContent().value;
                            const vsLink = itemContent && itemContent.details.metadata.vsLink;
                            if (vsLink) {
                                window.location.href = vsLink;
                            }
                        },
                        text: Resources.OpenInVisualStudio
                    }
                ]);
            }

            collection.push([
                {
                    groupKey: "advanced",
                    iconProps: { iconName: "Shield" },
                    id: "security",
                    onActivate: this.getCommandExecutor("security", webEditingEnabled, item),
                    text: Resources.SecurityActionText
                }
            ]);

            // Add contributed menu items
            if (menuContributionIds) {
                const url = this.getItemUrl(item.path);
                const contentMetadata =
                    item.path === this.selectedItem.value.path &&
                    this.selectedItemContent &&
                    this.selectedItemContent.value &&
                    this.selectedItemContent.value.details.metadata;

                const contributionContext = {
                    gitRepository: null,
                    item: {
                        sourceProvider: "Tfvc",
                        path: item.path,
                        isFolder: item.isFolder,
                        isSymLink: item.isSymLink,
                        item: {
                            changeDate: item.lastCommit.value && item.lastCommit.value.date,
                            changeset: item.changesetId,
                            contentMetadata,
                            deletionId: item.deletionId,
                            isBranch: item.isBranch,
                            isPendingChange: item.isPendingChange,
                            serverItem: item.path,
                            url,
                            version: getTfvcVersionString({
                                version: item.changesetId.toFixed(),
                                versionOption: TfvcVersionOption.None,
                                versionType: TfvcVersionType.Changeset
                            }),
                            versionDescription: item.changesetId.toFixed()
                        },
                        url
                    },
                    version: this.version.version
                } as IFilesHubContributedMenuContext;
                contributionContext.getSourceItemContext = () => contributionContext;
                setRemoteSerializationSettings(contributionContext, { proxyFunctions: true });

                for (const menuContributionId of menuContributionIds) {
                    addMenuItems(this.pageContext, collection, menuContributionId, contributionContext);
                }
            }
        }

        return collection;
    }

    public getItemUrl(path: string, { version, absoluteUrl, tabId, anchor }: ItemUrlOptions = {}): string {
        const queryParams: IQueryParameter[] = [
            { name: "path", value: path },
            { name: "version", value: version || this.versionString }
        ];
        if (tabId) {
            queryParams.push({ name: "_a", value: tabId });
        }
        if (anchor) {
            queryParams.push({ name: "anchor", value: anchor });
        }

        const queryString = "?" + toQueryString(queryParams);
        let url = queryString;
        if (absoluteUrl) {
            url = window.location.origin + window.location.pathname + queryString;
        }

        return url;
    }

    public getRootItem(): ITfvcItem {
        return this.createTfvcItem({ path: tfvcRoot + this.projectName, isFolder: true } as IInternalItemDescriptor);
    }

    public getSelectedItemContent(): IReadonlyObservableValue<IGitItemContent | undefined> {
        if (!this.selectedItemContent) {
            const contentObservable = new ObservableValue<IGitItemContent | undefined>(undefined);
            this.selectedItemContent = contentObservable;

            const selectedItem = this.selectedItem.value;
            if (selectedItem.isNewItem) {
                const name = getFileName(selectedItem.path);
                contentObservable.value = {
                    commitId: "",
                    details: {
                        content: "",
                        contentTruncated: false,
                        objectId: "",
                        metadata: {
                            contentType: "text/plain",
                            extension: getFileExtension(name),
                            fileName: getFileName(name)
                        } as FileContentMetadata,
                        resourceUri: getTfvcResourceUri(this.version, selectedItem.path)
                    },
                    editContext: this.createFileEditContext(selectedItem, "")
                };
            } else if (!selectedItem.isFolder && !selectedItem.isInvalid) {
                this.getItemContentPromise(selectedItem.path, this.version).then(content => {
                    if (!content.details.metadata.isBinary) {
                        content.editContext = this.createFileEditContext(selectedItem, content.details.content);
                    }
                    contentObservable.value = content;
                });
            }
        }
        return this.selectedItemContent;
    }

    public getSelectedItemAnnotationBlocks(): IReadyableReadonlyObservableArray<IAnnotationBlock> {
        if (!this.selectedItemAnnotationBlocks) {
            const path = this.selectedItem.value.path;
            this.selectedItemAnnotationBlocks = new ReadyableObservableArray<IAnnotationBlock>();

            const legacyTfvcClient = getLegacyTfvcClient(this.pageContext);
            legacyTfvcClient.getFileHistory(this.projectId, this.selectedItem.value.path, this.versionString).then(historyEntries => {
                // Make sure we are still on the same file
                if (this.selectedItem.value.path === path) {
                    if (historyEntries.length === 0) {
                        this.selectedItemAnnotationBlocks!.ready.value = true;
                        return;
                    }

                    const routingService = getTfvcRoutingService(this.pageContext);
                    const history: IAnnotationHistoryEntry[] = historyEntries.map((entry, index) => ({
                        annotatePreviousUrl:
                            historyEntries.length > index + 1
                                ? routingService.getFilesAnnotateUrl(path, historyEntries[index + 1].changeList.version)
                                : undefined,
                        authorName: entry.changeList.ownerDisplayName,
                        changeUrl: routingService.getChangesetDetailsUrl(entry.changeList.changesetId),
                        comment: entry.changeList.comment,
                        date: entry.changeList.creationDate,
                        shortId: entry.changeList.version
                    }));

                    const getFileDiffs = (skip: number, top: number) => {
                        const diffParameters: AnnotateDiffParameters[] = [];
                        // We request diffs between every pair of entries in history,
                        // plus one fake diff between last entry and nothing, to fall back every line
                        for (let i = skip; i < Math.min(historyEntries.length, skip + top); i++) {
                            diffParameters.push({
                                mFileId: historyEntries[i].fileId,
                                mEncoding: historyEntries[i].encoding,
                                mServerItem: historyEntries[i].serverItem,
                                oFileId: i + 1 < historyEntries.length ? historyEntries[i + 1].fileId : undefined,
                                oEncoding: i + 1 < historyEntries.length ? historyEntries[i + 1].encoding : undefined,
                                oServerItem: i + 1 < historyEntries.length ? historyEntries[i + 1].serverItem : undefined
                            });
                        }

                        return legacyTfvcClient.getAnnotateDiffs(this.projectId, diffParameters).then(diffs => {
                            return diffs.map((diff, i) => {
                                return {
                                    mObjectId: historyEntries[i].fileId.toFixed(),
                                    diffs: [
                                        {
                                            oObjectId: historyEntries.length > i + 1 ? historyEntries[i + 1].fileId.toFixed() : undefined,
                                            diff
                                        }
                                    ]
                                };
                            });
                        });
                    };

                    loadAnnotationBlocks(this.selectedItem, this.selectedItemAnnotationBlocks!, history, getFileDiffs);
                }
            });
        }

        return this.selectedItemAnnotationBlocks;
    }

    public getItemContent(path: string, version?: TfvcVersionDescriptor): IReadonlyObservableValue<IGitItemContent | undefined> {
        // Use selected item if it matches. This covers when the item is new and we should not try to fetch content to show Preview tab.
        if (
            this.selectedItemContent &&
            this.selectedItem &&
            this.selectedItem.value &&
            this.selectedItem.value.path === path &&
            (!version || this.versionString === getTfvcVersionString(version))
        ) {
            return this.selectedItemContent;
        }

        const contentObservable = new ObservableValue<IGitItemContent | undefined>(undefined);
        this.getItemContentPromise(path, version || this.version).then(content => {
            contentObservable.value = content;
        });
        return contentObservable;
    }

    public getFileDownloadUrl(path: string, version?: TfvcVersionDescriptor, forceDownload?: boolean): string {
        return this.getDownloadUrlForItem(path, version || this.version, false, !!forceDownload);
    }

    public getUrlTransformer(path: string): IFilesHubUrlTransformer {
        return new MarkdownFilesHubUrlTransformer(
            path,
            false,
            versionString => versionString === this.versionString,
            (path, anchor) => this.getItemUrl(path, { anchor }),
            path => this.getDownloadUrlForItem(path, this.version, false, false)
        );
    }

    private getDownloadUrlForItem(path: string, version: TfvcVersionDescriptor, isFolder: boolean, forceDownload: boolean): string {
        const hostPath = this.pageContext.getService<IVssPageService>("IVssPageService").getData()!.hostPath;
        const url = new Uri(combineUrlPaths(hostPath, `${this.projectId}/_apis/tfvc/items`));

        url.addQueryParams({
            path: path,
            ["versionDescriptor[versionOptions]"]: version.versionOption || 0,
            ["versionDescriptor[versionType]"]: version.versionType || 0,
            ["versionDescriptor[version]"]: version.version,
            $format: isFolder ? "zip" : "octetStream",
            ["api-version"]: "5.0"
        });

        if (forceDownload) {
            url.addQueryParam("download", "true");
        }

        return url.absoluteUri;
    }

    private createFileEditContext(item: ITfvcItem, originalContent: string): FileEditContext {
        const context = new FileEditContext(
            originalContent,
            () => {
                showMessageDialog(this.pageContext, {
                    message: format(Resources.UnsavedFileFormat, item.path),
                    onClose: result => {
                        if (result) {
                            // Reset the content to its original value
                            context.setContent(originalContent);

                            // Turn off edit mode
                            this.setEditMode(false);

                            // Trigger selected-item-change event to allow
                            // components to re-render
                            this.updateSelectedItem(item);
                        }
                    },
                    okButtonProps: {
                        danger: true,
                        primary: false,
                        text: Resources.Discard
                    },
                    title: Resources.UnsavedChanges
                });
            },
            content => {
                this.executeFileActionCommand("checkin", item, content);
            }
        );
        return context;
    }

    private getCommandExecutor(commandName: string, webEditingEnabled: boolean, ...args: any[]): { (): void } {
        if (!webEditingEnabled) {
            return () =>
                getReposMessagesService(this.pageContext).showMessage({
                    renderMessage: () => Resources.TfvcEditDisabledDialogText,
                    severity: MessageCardSeverity.Warning
                });
        } else if (commandName === "edit") {
            return () => this.setSelectedItem(args[0], true);
        } else {
            return (): void => {
                this.executeFileActionCommand(commandName, ...args);
            };
        }
    }

    private executeFileActionCommand(commandName: string, ...args: any[]): void {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        contributionService.executeCommandEx(
            "ms.vss-code-web.tfvc-files-hub-actions-command",
            {
                dependencies: ["ms.vss-code-web.tfvc-files-hub-actions-content"],
                serviceName: "ITfvcFilesHubActionsService",
                methodName: commandName
            },
            ...args
        );
    }

    private processItems(items: { [path: string]: IInternalItemDescriptor[] }): void {
        const folderUpdates: IFolderContents[] = [];
        const childArrays: ReadyableObservableArray<ITfvcItem>[] = [];

        // Process all of our results
        for (const parentPath in items) {
            const result = this.processChildItems(parentPath, items[parentPath]);
            if (result) {
                childArrays.push(result);

                // Add true folder items to our list of folder updates to send
                const parentItem = this.itemsByPath[parentPath];
                if (parentItem && parentItem.isFolder) {
                    folderUpdates.push({ path: parentPath, children: result.value });
                }
            }
        }

        // Send folder update events, ordered from items closest to the root to the longest path
        folderUpdates.sort((f1, f2) => f1.path.length - f2.path.length);
        this.loadedFolders.push(...folderUpdates);

        // Update the underlying readyable arrays
        for (const childArray of childArrays) {
            if (!childArray.ready.value) {
                childArray.ready.value = true;
            }
        }
    }

    private processChildItems(parentPath: string, childItems: IInternalItemDescriptor[]): ReadyableObservableArray<ITfvcItem> | undefined {
        const parentPathItems: ITfvcItem[] = [];
        const parentPathWithSlashEnd = parentPath[parentPath.length - 1] === "/" ? parentPath : parentPath + "/";

        for (const childItem of childItems) {
            const tfvcItem = this.createTfvcItem(childItem);

            // The first item in each data provider entry for the folder is the folder item itself. Don't process it as a child item.
            if (tfvcItem.path !== parentPath) {
                // Do a check for empty intermediate folders. For example, if
                // /Root contains a folder A which contains only a folder B but
                // no individual files, then we will see /Root/A and /Root/A/B in
                // the list of children here. Combine these into a single item with
                // the name "A/B".
                if (
                    tfvcItem.isFolder &&
                    parentPathItems.length &&
                    parentPathItems[parentPathItems.length - 1].isFolder &&
                    tfvcItem.path.startsWith(parentPathItems[parentPathItems.length - 1].path + "/")
                ) {
                    parentPathItems.pop();
                    parentPathItems.push({
                        ...tfvcItem,
                        name: tfvcItem.path.substr(parentPathWithSlashEnd.length)
                    });
                } else {
                    parentPathItems.push(tfvcItem);
                }

                // If we load now the selected item, replace it which will refresh Contents tab
                if (tfvcItem.path === this.selectedItem.value.path) {
                    this.setSelectedItem(tfvcItem);
                }
            }
        }

        parentPathItems.sort(this.sortItems);

        let itemsObservableArray = this.itemsByParentPath[parentPath];
        if (!itemsObservableArray) {
            itemsObservableArray = new ReadyableObservableArray();
            this.itemsByParentPath[parentPath] = itemsObservableArray;
        }
        itemsObservableArray.value = parentPathItems;

        return itemsObservableArray;
    }

    private updateSelectedItem(item: ITfvcItem) {
        this.selectedItemContent = undefined;
        this.selectedItemAnnotationBlocks = undefined;

        if (this.selectedItem.value.isNewItem) {
            // If we were on a new item, then perform an FPS to the new item in order to clear out
            // the new item entries.
            let newPath = item.path;

            // Pick the first non-new path to navigate to
            while (newPath && newPath !== this.getRootItem().path) {
                const item = this.itemsByPath[newPath];
                if (!(item && item.isNewItem)) {
                    break;
                }
                newPath = getParentPath(newPath, tfvcRoot)!;
            }

            FastPageSwitch(this.pageContext, this.getItemUrl(newPath), false);
        } else {
            this.selectedItem.value = item;

            const navService = this.pageContext.getService<ITfsNavigationService>("ITfsNavigationService");
            navService.setPageTitle(item.name);
        }
    }

    private getItemContentPromise(path: string, version: TfvcVersionDescriptor): Promise<IGitItemContent> {
        return getTfvcFilesService(this.pageContext).getItemContentPromise(path, version);
    }

    private getItem(path: string): ITfvcItem {
        let item = this.itemsByPath[path];
        if (!item) {
            // Look for the item ignoring casing
            item = this.itemsByPath[path.toLocaleLowerCase()];

            if (!item) {
                // Item does not exist. Create a entry for the invalid item
                item = this.createTfvcItem({ path: path, isFolder: false, isInvalid: true } as IInternalItemDescriptor);
            }
        }
        return item;
    }

    private createTfvcItem(item: IInternalItemDescriptor): ITfvcItem {
        const existingItem = this.itemsByPath[item.path];
        if (existingItem) {
            return existingItem;
        }

        // Roots are either "$/" or "$/Project"
        const isRoot = item.path.indexOf("/", 2) === -1;
        const name = isRoot ? item.path : getFileName(item.path);
        const tfvcItem = {
            commitId: item.version ? item.version.toFixed() : "",
            iconProps: isRoot
                ? { iconName: "TFVCLogo" }
                : item.isBranch
                ? { iconName: "OpenSource" }
                : item.isFolder
                ? { iconName: "FabricFolderFill", className: "repos-folder-icon" }
                : item.isSymLink
                ? { iconName: "PageArrowRight" }
                : { iconName: getIconNameForFile(name) },
            isInvalid: item.isInvalid,
            isFolder: item.isFolder,
            isNewItem: item.isNewItem,
            isSymLink: item.isSymLink,
            changesetId: item.version,
            encoding: item.encoding,
            lastCommit: new ObservableValue(undefined),
            href: this.getItemUrl(item.path),
            name: name,
            path: item.path
        };

        // Only cache the item if we know its commit id (or this is a new item the user is creating).
        // Otherwise it is a "fake" item we created where we only know the path.
        if (item.version || item.isNewItem) {
            this.itemsByPath[item.path] = tfvcItem;

            // Also cache the all-lowercase path so that we can find items with case insensitivity
            const lowerCasePath = item.path.toLocaleLowerCase();
            if (lowerCasePath !== item.path && !this.itemsByPath[lowerCasePath]) {
                this.itemsByPath[lowerCasePath] = tfvcItem;
            }
        }

        return tfvcItem;
    }

    private ensureChangesetsFetched(path: string) {
        getLegacyTfvcClient(this.pageContext)
            .getFolderLatestChangesets(this.projectId, path, this.versionString)
            .then(changesets => {
                const changesetsById: { [version: string]: IChangeset } = {};
                for (const changeset of changesets) {
                    changesetsById[changeset.version] = changeset;
                }

                const routingService = getTfvcRoutingService(this.pageContext);

                for (const item of this.itemsByParentPath[path].value) {
                    const version = item.commitId;
                    if (version) {
                        const changeset = changesetsById[version];
                        if (changeset) {
                            item.lastCommit.value = {
                                comment: changeset.comment,
                                date: changeset.creationDate,
                                commitId: version,
                                href: routingService.getChangesetDetailsUrl(item.changesetId),
                                name: changeset.ownerDisplayName,
                                shortCommitId: version
                            };
                        }
                    }
                }
            });
    }

    private fixPath(path: string): string {
        const root = this.getRootItem().path;

        if (path && path.startsWith(root)) {
            path = path.substring(root.length);
        }

        if (!path) {
            return root;
        }

        let fixedPath = trimTrailingSlash(trimLeadingSlash((path || "").replace(/\\/g, "/"), tfvcRoot));

        // just want to make sure we are covered no matter what we get
        if (fixedPath.startsWith("/")) {
            fixedPath = root + fixedPath;
        } else {
            fixedPath = root + "/" + fixedPath;
        }

        return fixedPath;
    }

    private sortItems = (i1: ITfvcItem, i2: ITfvcItem): number => {
        return i1.isFolder === i2.isFolder ? localeIgnoreCaseComparer(i1.name, i2.name) : i1.isFolder ? -1 : 1;
    };
}

export function getFilesHubViewService(pageContext: IVssPageContext): IFilesHubViewService {
    return pageContext.getService<IFilesHubViewService>("ITfvcFilesHubViewService");
}

Services.add("ITfvcFilesHubViewService", { serviceFactory: FilesHubViewService });

class FilesHubTabsService extends FilesHubTabsServiceBase {
    protected getTabRegistrations(): IFilesHubTabRegistration[] {
        return tabRegistrations;
    }

    protected getFilesHubService(pageContext: IVssPageContext) {
        return getFilesHubViewService(pageContext);
    }
}

const tabRegistrations: IFilesHubTabRegistration[] = [];

export function registerFilesHubTab(tabRegistration: IFilesHubTabRegistration): void {
    tabRegistrations.push(tabRegistration);
}

export function getFilesHubTabsService(pageContext: IVssPageContext): IFilesHubTabsService {
    return pageContext.getService<IFilesHubTabsService>("ITfvcFilesHubTabsService");
}

Services.add("ITfvcFilesHubTabsService", { serviceFactory: FilesHubTabsService });
